import React, { useState } from 'react'
import { OHFooter } from '../../components/openhand'

import { apiConnector } from '../../services/apiConnector'
import toast from 'react-hot-toast'
import {
  FiCheck,
  FiClock,
  FiChevronDown,
  FiMessageSquare,
  FiArrowRight,
  FiShield,
  FiCheckCircle,
  FiZap,
  FiSend
} from 'react-icons/fi'

export function ContactUs() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    workType: 'Life or executive coaching',
    message: '',
  })
  const [selectedTopic, setSelectedTopic] = useState(null)
  // Single active accordion state matching Questions Practitioners Ask & AURA
  const [activeFaq, setActiveFaq] = useState(0)
  const [loading, setLoading] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  const topics = [
    {
      id: 'fit',
      heading: 'Queries Related to Setup',
      dur: '25 minutes',
      title: `"I'm not sure this fits my practice"`,
      workTypeValue: 'Life or executive coaching',
      desc: `You work in a specific way — somatic, narrative, faith-based, group-only — and you've been burned by tools that assumed you sell courses.`,
      points: [
        'We look at your actual practice format',
        "We'll tell you honestly if we're wrong for you",
        'No slide decks or forced sales demos',
      ],
      founderHint: 'Recommended for Practice Setup',
    },
    {
      id: 'migration',
      heading: 'Queries Related to Technology and Ethics',
      dur: '40 minutes',
      title: `"I have learners already — how do I move?"`,
      workTypeValue: 'Counselling or psychotherapy',
      desc: `You're running on WhatsApp, Google Meet, spreadsheets, and manual payment links. It works, but it's exhausting.`,
      points: [
        'A single transition window, zero dropped bookings',
        'Your existing learner records moved smoothly',
        'Nothing goes live until you explicitly approve',
      ],
      founderHint: 'Recommended for Caseload Migration',
    },
    {
      id: 'org',
      heading: 'Queries Related to B2B',
      dur: '45 minutes',
      title: `"We want this for our employees"`,
      workTypeValue: 'Organisational / employee wellbeing',
      desc: `You're an HR or people leader. Your traditional EAP exists, nobody uses it, and you need something employees will actually open up in.`,
      points: [
        'Pilot container scoped for one team first',
        'Reporting that strictly respects individual confidentiality',
        'Vetted practitioner panel, or bring your own',
      ],
      founderHint: 'Recommended for Org Leads',
    },
  ]

  const faqs = [
    {
      q: 'Do I have to talk to anyone to start?',
      a: 'No. The free plan opens immediately without requiring a call, a credit card, or a demo. This page exists for practitioners who want a direct human conversation first — not as a paywall or gate in front of the product.',
    },
    {
      q: 'Is my learner data safe if I move here?',
      a: 'Learner records are end-to-end encrypted, access-controlled strictly per practitioner, and never used to train external public models. Bring this up on your call — our technical team will walk you through exactly where data resides and what you can delete.',
    },
    {
      q: 'What if I already use TagMango, Topmate or my own site?',
      a: "Plenty of practitioners run both concurrently for a transition phase. We'll assist you in migrating your calendar, your offers, and your existing learners without any mandatory hard cutover date.",
    },
    {
      q: 'Can I turn the in-session AURA off?',
      a: "Yes, entirely — at either the account level or on a per-session basis. It is opt-in for you and separately consented by your learner. OpenHand sessions function seamlessly with or without it.",
    },
  ]

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.id]: e.target.value }))
  }

  // Sync selected topic with workType
  const handleSelectTopic = (index) => {
    setSelectedTopic(index)
    const topic = topics[index]
    if (topic) {
      setFormData((prev) => ({
        ...prev,
        workType: topic.workTypeValue,
      }))
    }
    // Scroll smoothly to form
    const bookElem = document.getElementById('book')
    if (bookElem) {
      bookElem.scrollIntoView({ behavior: 'smooth' })
    }
  }

  // Single open accordion behavior
  const toggleFaq = (index) => {
    setActiveFaq((prev) => (prev === index ? null : index))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!formData.name || !formData.email) {
      toast.error('Please provide your name and email so we can reply.')
      return
    }

    setLoading(true)
    try {
      const selectedTopicObj = selectedTopic !== null ? topics[selectedTopic] : null
      const topicTitle = selectedTopicObj ? selectedTopicObj.title : 'General Inquiry'
      const fullMessage = selectedTopicObj ? `[Focus Topic: ${topicTitle}]\n${formData.message}` : formData.message

      const res = await apiConnector('POST', '/api/v1/reach/contact', {
        name: formData.name,
        email: formData.email,
        workType: formData.workType,
        message: fullMessage,
        meetingType: 'Requested Call',
      })

      const isOrgInquiry =
        formData.workType === 'Organisational / employee wellbeing' ||
        (selectedTopicObj?.id === 'org')

      if (isOrgInquiry) {
        try {
          await apiConnector('POST', '/api/v1/org/book-conversation', {
            organizationName: formData.workType || 'Organisation',
            contactName: formData.name,
            contactEmail: formData.email,
            message: fullMessage,
            interestedIn: selectedTopicObj ? [selectedTopicObj.title] : ['Employee Wellbeing'],
          })
        } catch (orgErr) {
          console.warn('OrgConversation log failed (non-critical):', orgErr.message)
        }
      }

      if (res?.data?.success) {
        setSubmitted(true)
        toast.success('Contact message sent successfully!')
      } else {
        setSubmitted(true)
      }
    } catch (err) {
      console.error('Contact us submit error:', err)
      setSubmitted(true)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="talk-page relative min-h-screen">

      {/* Hero */}
      <header className="talk-hero">
        <div className="oh-wrap">
          <div className="talk-status-badge">
            <span className="talk-pulse-dot"></span>
            <span>Contact Us — Response within 24 hours guaranteed</span>
          </div>

          <h1
            className="text-2xl sm:text-5xl lg:text-6xl font-black tracking-tight text-slate-900 my-4 text-center whitespace-nowrap w-full mx-auto"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            Contact <span className="italic font-bold" style={{ color: '#2563EB' }}>OpenHand Team.</span>
          </h1>
          <p className="sub text-slate-600 text-base sm:text-lg max-w-2xl mx-auto font-medium leading-relaxed mb-8">
            Have questions about OpenHand? Connect directly with our team for personalized practice guidance, platform setup, or enterprise inquiries.
          </p>

          {/* Key Assurance Stats */}
          <div className="talk-hero-stats grid grid-cols-2 sm:flex sm:flex-row rounded-2xl sm:rounded-full gap-4 sm:gap-6 px-6 sm:px-10 py-4">
            <div className="stat-item">
              <span className="stat-num">24h SLA</span>
              <span className="stat-lbl">Guaranteed Response</span>
            </div>
            <div className="stat-divider hidden sm:block"></div>
            <div className="stat-item">
              <span className="stat-num">1:1 Support</span>
              <span className="stat-lbl">Dedicated Assistance</span>
            </div>
            <div className="stat-divider hidden sm:block"></div>
            <div className="stat-item">
              <span className="stat-num">0%</span>
              <span className="stat-lbl">Sales Push</span>
            </div>
            <div className="stat-divider hidden sm:block"></div>
            <div className="stat-item">
              <span className="stat-num">100%</span>
              <span className="stat-lbl">Private &amp; Confidential</span>
            </div>
          </div>
        </div>
      </header>



      {/* Three Conversations Section (ITEM 8 FIX) */}
      <section className="talk-sec">
        <div className="oh-wrap">
          <div className="talk-sec-head">
            <span className="talk-section-tag">Tailored Call Formats</span>
            <h2>Three conversations we actually have</h2>
            <p>Pick whichever is closest to your needs. Click any topic to select it for your booking.</p>
          </div>

          <div className="talk-ways grid grid-cols-1 md:grid-cols-3 gap-5">
            {topics.map((t, idx) => {
              const isSelected = selectedTopic === idx
              return (
                <div
                  key={t.id}
                  className={`talk-way ${isSelected ? 'selected-way' : ''}`}
                  onClick={() => handleSelectTopic(idx)}
                >
                  {isSelected && (
                    <div className="absolute top-3.5 right-4">
                      <span className="way-selected-badge">
                        <FiCheck /> Selected
                      </span>
                    </div>
                  )}
                  <h3
                    style={{
                      fontFamily: "'Playfair Display', Georgia, serif",
                      fontSize: '23px',
                      fontWeight: 800,
                      color: '#0F172A',
                      textAlign: 'center',
                      margin: '6px 0 10px 0',
                      lineHeight: 1.25,
                    }}
                  >
                    {t.heading}
                  </h3>
                  <div className="flex items-center justify-center mb-3">
                    <span className="dur">
                      <FiClock style={{ display: 'inline', marginRight: '4px' }} />
                      {t.dur}
                    </span>
                  </div>
                  <div
                    style={{
                      fontSize: '14px',
                      fontStyle: 'italic',
                      color: '#4F46E5',
                      fontWeight: 600,
                      textAlign: 'center',
                      marginBottom: '12px',
                    }}
                  >
                    {t.title}
                  </div>
                  <p>{t.desc}</p>
                  <ul>
                    {t.points.map((pt, pIdx) => (
                      <li key={pIdx}>{pt}</li>
                    ))}
                  </ul>
                  <div className="way-footer">
                    <span className="hint-pill">{t.founderHint}</span>
                    <button type="button" className="way-action-btn">
                      {isSelected ? 'Selected Topic' : 'Choose Topic'} <FiArrowRight />
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* Book Form Section */}
      <section className="talk-sec" id="book">
        <div className="oh-wrap">
          <div className="talk-formsec">
            <div className="talk-formgrid grid grid-cols-1 lg:grid-cols-[1fr_1.1fr] gap-8">
              <div className="form-info-col">
                <span className="form-eyebrow">
                  <FiZap className="sparkle-icon" /> Direct Access
                </span>
                <h2>Contact Us</h2>
                <p className="lede">
                  Tell us roughly where you are in your practice journey. We'll send two or three proposed time slots in your timezone within 24 hours.
                </p>

                {/* Selected Topic Context Indicator */}
                {selectedTopic !== null && (
                  <div className="topic-context-card">
                    <div className="topic-context-head">
                      <FiMessageSquare className="tc-icon" />
                      <span>Focus Topic Selected:</span>
                    </div>
                    <strong>{topics[selectedTopic].title}</strong>
                    <button
                      type="button"
                      className="topic-context-reset"
                      onClick={() => setSelectedTopic(null)}
                    >
                      Change topic
                    </button>
                  </div>
                )}

                <div className="talk-assure">
                  <div>
                    <FiCheckCircle />
                    <p>
                      <strong>A founder joins the call.</strong> Every single time — never an SDR or sales rep.
                    </p>
                  </div>
                  <div>
                    <FiShield />
                    <p>
                      <strong>Honest assessment.</strong> If OpenHand isn't right for your practice, we'll say so immediately and recommend better alternatives.
                    </p>
                  </div>
                  <div>
                    <FiZap />
                    <p>
                      <strong>Zero spam sequence.</strong> One prompt reply from us with times, then the ball is entirely in your court.
                    </p>
                  </div>
                </div>
              </div>

              <div className="form-fields-col">
                {submitted ? (
                  /* ITEM 9 FIX: Highlighted Submit Another Request Button */
                  <div className="talk-ok-card p-8 rounded-2xl bg-slate-900 border border-emerald-500/30 text-center">
                    <div className="w-16 h-16 rounded-full bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center text-emerald-400 mx-auto mb-4">
                      <FiCheckCircle className="w-8 h-8" />
                    </div>
                    <h3 className="text-2xl font-extrabold text-white mb-2">Contact request sent!</h3>
                    <p className="text-slate-300 text-sm mb-6">
                      Thanks, <strong className="text-white">{formData.name.split(' ')[0]}</strong>. We have received your request.
                    </p>
                    <div className="bg-slate-950 p-4 rounded-xl text-xs space-y-2 mb-6 text-left border border-slate-800">
                      <div className="flex justify-between">
                        <span className="text-slate-400">Reply Email:</span>
                        <strong className="text-slate-200">{formData.email}</strong>
                      </div>

                      {selectedTopic !== null && (
                        <div className="flex justify-between">
                          <span className="text-slate-400">Focus Topic:</span>
                          <strong className="text-slate-200">{topics[selectedTopic].title}</strong>
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      className="w-full py-3.5 px-6 rounded-xl font-bold text-sm bg-blue-600 hover:bg-blue-500 text-white transition-all shadow-lg shadow-blue-600/30 flex items-center justify-center gap-2"
                      onClick={() => {
                        setSubmitted(false)
                        setFormData({
                          name: '',
                          email: '',
                          workType: 'Life or executive coaching',
                          message: '',
                        })
                        setSelectedTopic(null)
                      }}
                    >
                      Submit Another Request <FiSend />
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="talk-fields">
                    <div className="form-row2">
                      <div>
                        <label htmlFor="name">Your Name</label>
                        <input
                          id="name"
                          type="text"
                          placeholder="Enter your name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                        />
                      </div>

                      <div>
                        <label htmlFor="email">Work Email</label>
                        <input
                          id="email"
                          type="email"
                          placeholder="you@practice.in"
                          value={formData.email}
                          onChange={handleChange}
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="workType">What kind of practice do you lead?</label>
                      <select id="workType" value={formData.workType} onChange={handleChange}>
                        <option>Life or executive coaching</option>
                        <option>Counselling or psychotherapy</option>
                        <option>Relationship or family work</option>
                        <option>Wellness, healing or somatic practice</option>
                        <option>Organisational / employee wellbeing</option>
                        <option>Something else</option>
                      </select>
                    </div>

                    <div>
                      <label htmlFor="message">Anything specific to share before the call?</label>
                      <textarea
                        id="message"
                        rows="3"
                        placeholder="e.g. I run two Circles a year and see about eight 1:1 learners. Mostly curious about learner data confidentiality."
                        value={formData.message}
                        onChange={handleChange}
                      />
                    </div>

                    <button type="submit" disabled={loading} className="talk-btn">
                      {loading ? (
                        <>
                          <span className="btn-spinner"></span> Sending Request…
                        </>
                      ) : (
                        <>
                          <FiSend style={{ marginRight: '8px' }} /> Send Message
                        </>
                      )}
                    </button>

                    <p className="talk-fineprint">
                      <FiShield style={{ display: 'inline', marginRight: '4px', color: '#9BB4FF' }} />
                      We reply within one working day. Your details are strictly confidential.
                    </p>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Accordion Section (ITEM 10 FIX) */}
      <section className="talk-sec">
        <div className="oh-wrap">
          <div className="talk-sec-head">
            <span className="talk-section-tag">Frequently Asked Questions</span>
            <h2 style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Before you ask us</h2>
            <p>Common questions practitioners have before scheduling a founder call.</p>
          </div>

          <div className="talk-faq">
            {faqs.map((faq, idx) => {
              const isOpen = activeFaq === idx
              return (
                <div
                  key={idx}
                  className={`talk-q-card rounded-2xl p-5 transition-all cursor-pointer border ${
                    isOpen
                      ? 'bg-slate-900 border-blue-500/50 shadow-lg shadow-blue-500/10'
                      : 'bg-slate-900/60 border-slate-800 hover:border-slate-700'
                  }`}
                  onClick={() => toggleFaq(idx)}
                >
                  <div className="talk-q-head flex items-center justify-between gap-4">
                    <h3 className="text-base font-bold text-white">{faq.q}</h3>
                    <span className="talk-q-toggle text-slate-400 shrink-0">
                      <FiChevronDown className={`w-5 h-5 transition-transform ${isOpen ? 'rotate-180 text-blue-400' : ''}`} />
                    </span>
                  </div>
                  {isOpen && (
                    <div className="talk-q-body mt-3 pt-3 border-t border-slate-800 text-sm text-slate-300 leading-relaxed">
                      <p>{faq.a}</p>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      </section>



      <OHFooter />
    </div>
  )
}

export default ContactUs
